import { CartItem } from '@/api';
import { getPoster } from '@/app/utils/item-utils';
import ArtistTitle from '@/components/general/ArtistTitle';
import SmallLogo from '@/components/general/LogoSmall';
import Price from '@/components/general/Price';
import { ResponsiveImage } from '@/components/general/ResponsiveImage';
import If, { Else } from '@/components/util/IF';
import useAuth from '@/contexts/AuthContext';
import useCart from '@/hooks/useCart';
import i18next from 'i18next';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useCallback, useMemo, useState } from 'react';

type Props = {
    cartItem: CartItem;
};

const CartRow = ({ cartItem }: Props) => {
    const { user } = useAuth();
    const { item } = cartItem;
    const router = useRouter();
    const { removeItem, updateQuantity } = useCart();
    const [updating, setUpdating] = useState<boolean>(false);

    const [quantity, setQuantity] = React.useState<number>(cartItem.quantity);

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { private: isPrivate, images, private_preview_image_url, slug, artists } = item;
    const firstImage = useMemo(() => getPoster(images), [images]);
    const renderPrivateImage = (isPrivate && !user);
    let { name } = item;
    if (isPrivate && !user) {
        name = i18next.t('item-text.private-title');
    }

    const updateQuantityHandler = useCallback(async (remove = false) => {
        try {
            setUpdating(true);
            if (remove || quantity === 0) {
                await removeItem(cartItem.item.id);
            } else {
                await updateQuantity(cartItem.item.id, quantity); // ToDo: error handler in useCart
                setUpdating(false);
            }
        } catch (error) {
            setQuantity(cartItem.quantity);
            setUpdating(false);
        }
    }, [cartItem.item.id, cartItem.quantity, quantity, removeItem, updateQuantity]);

    return (
        <div className="flex flex-row justify-between gap-[16px]">
            <div className="">
                <If condition={renderPrivateImage && !private_preview_image_url}>
                    <SmallLogo className="cursor-pointer object-cover h-[96px] w-[96px] max-h-[96px] min-w-[96px] block p-12 [&>path]:fill-blue2" />
                    <Else>
                        <ResponsiveImage
                            onClick={() => router.push(`/item/${slug}`)}
                            alt={name}
                            src={renderPrivateImage ? private_preview_image_url : firstImage?.url}
                            imageWidth={96}
                            width={96}
                            height={96}
                            className="cursor-pointer object-cover h-[96px] w-[96px] max-h-[96px] min-w-[96px] block rounded-[8px]" />
                    </Else>
                </If>
            </div>
            <div className="h-auto flex flex-col justify-between w-full">
                <div className="">
                    <h3 className="text-[16px] font-[500] leading-[20px] tracking-[-0.2px] cursor-pointer"><Link href={`/item/${slug}`}>{name}</Link></h3>
                    <If condition={artists.length > 0}>
                        <ArtistTitle artist={artists?.[0]} variant={0} />
                        <Else>
                            <span className="text-[14px] font-[500] mt-[-4px] block">&nbsp;</span>
                        </Else>
                    </If>
                </div>
                <button className="text-[12px] text-[#8F97AA] inline-block w-fit underline font-[500]" onClick={() => updateQuantityHandler(true)}>
                    {i18next.t('cart.remove-from-cart')}
                </button>
            </div>
            <div className="min-w-[69px] max-w-[69px] text-[16px] font-[500] flex flex-col justify-between items-center">
                <Price number={cartItem.item_amount} />
                <form onSubmit={async (e) => {
                    e.preventDefault();
                    updateQuantityHandler();
                }}>

                    <div className="relative">
                        <If condition={updating}>
                            <div className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-10">
                                <svg aria-hidden="true" className="w-5 h-5 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#ccc" />
                                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="black" />
                                </svg>
                            </div>
                        </If>
                        <input
                            style={{ visibility: updating ? 'hidden' : 'visible' }}
                            disabled={updating}
                            type="number"
                            onChange={(e) => {
                                setQuantity(Number(e.target.value));
                            }}
                            onBlur={() => updateQuantityHandler()}
                            value={quantity}
                            className="w-[100%] rounded-[10px]  border-[1px] border-[#D0D5DD] disabled:opacity-20" />
                    </div>

                </form>
            </div>
        </div>
    );
};

export default CartRow;
